import './axios-augment';
import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type { MutationHandlers } from './mutations-offline';
import type { RouteMatcher } from './routes';

export interface OfflineInterceptorOptions {
  isOffline: () => boolean;
  routes: RouteMatcher;
  mutations: MutationHandlers;
  scheduleSync?: (client: AxiosInstance) => void;
  e2eSkip?: () => boolean;
  /** Сеть или 5xx: отдать чтение из локального кэша вместо ошибки. */
  isUnreachableError?: (error: unknown) => boolean;
  onUnreachableError?: (error: unknown) => void;
}

function isMutation(method?: string): boolean {
  const m = (method ?? 'get').toUpperCase();
  return m === 'POST' || m === 'PATCH' || m === 'DELETE';
}

function offlineAdapter(config: InternalAxiosRequestConfig, data: unknown): Promise<AxiosResponse> {
  config.servedFromLocal = true;
  return Promise.resolve({
    data,
    status: 200,
    statusText: 'OK (Cache)',
    headers: {},
    config,
  });
}

function throwCacheMiss(config: InternalAxiosRequestConfig): never {
  throw new axios.AxiosError('offline_cache_miss', 'OFFLINE_CACHE_MISS', config);
}

function throwNotSupported(config: InternalAxiosRequestConfig): never {
  throw new axios.AxiosError('Действие недоступно офлайн', 'OFFLINE_NOT_SUPPORTED', config);
}

function hasCachedPayload(data: unknown): boolean {
  if (data == null) return false;
  if (Array.isArray(data)) return data.length > 0;
  return true;
}

export function createOfflineInterceptors(options: OfflineInterceptorOptions) {
  const { isOffline, routes, mutations, scheduleSync, e2eSkip, isUnreachableError, onUnreachableError } =
    options;
  const { isOnlineOnly, matchRoute } = routes;

  async function cachedGetAfterFailure(error: unknown): Promise<AxiosResponse | null> {
    if (!isUnreachableError || !axios.isAxiosError(error)) return null;
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return null;

    const config = error.config;
    if (!config || e2eSkip?.() || config.skipOffline || config.servedFromLocal) return null;

    const method = (config.method ?? 'get').toUpperCase();
    const url = config.url ?? '';
    if (method !== 'GET' || isOnlineOnly(url) || !isUnreachableError(error)) return null;

    try {
      const params = config.params as Record<string, unknown> | undefined;
      const data = await mutations.handleOfflineGet(url, params);
      if (!hasCachedPayload(data)) return null;
      onUnreachableError?.(error);
      return await offlineAdapter(config, data);
    } catch {
      return null;
    }
  }

  function install(client: AxiosInstance): void {
    client.interceptors.request.use(async (config) => {
      if (e2eSkip?.() || config.skipOffline) return config;

      const url = config.url ?? '';
      if (isOnlineOnly(url)) return config;

      const method = (config.method ?? 'get').toUpperCase();

      if (method === 'GET' && !config.skipCache && isOffline()) {
        try {
          const params = config.params as Record<string, unknown> | undefined;
          const data = await mutations.handleOfflineGet(url, params);
          config.adapter = () => offlineAdapter(config, data);
        } catch (err) {
          if (err instanceof Error && err.message === 'offline_cache_miss') {
            throwCacheMiss(config);
          } else {
            throw err;
          }
        }
        return config;
      }

      if (isMutation(method)) {
        const route = matchRoute(method, url);
        if (route?.requiresNetwork && !isOffline()) {
          return config;
        }

        try {
          const payload = config.data
            ? ((typeof config.data === 'string'
                ? JSON.parse(config.data)
                : config.data) as unknown)
            : undefined;
          const params = config.params as Record<string, unknown> | undefined;
          const data = await mutations.handleOfflineMutation(method, url, payload, params);
          config.adapter = () => offlineAdapter(config, data ?? null);
          if (!isOffline()) {
            scheduleSync?.(client);
          }
        } catch (err) {
          if (err instanceof Error && err.message === 'offline_not_supported') {
            if (isOffline()) throwNotSupported(config);
            return config;
          }
          if (err instanceof Error && err.message === 'offline_cache_miss') {
            if (isOffline()) throwCacheMiss(config);
            return config;
          }
          throw err;
        }
      }

      return config;
    });

    client.interceptors.response.use(async (response) => {
      const config = response.config;
      if (config.servedFromLocal) return response;

      const url = config.url ?? '';
      const method = (config.method ?? 'get').toUpperCase();

      const requestData = config.data
        ? ((typeof config.data === 'string'
            ? JSON.parse(config.data)
            : config.data) as Record<string, unknown>)
        : undefined;

      if (!isOnlineOnly(url) && method === 'GET') {
        await mutations.cacheOnlineResponse(
          method,
          url,
          response.data,
          config.params as Record<string, unknown>,
        );
      }

      if (!isOnlineOnly(url) && isMutation(config.method)) {
        await mutations.cacheOnlineResponse(method, url, response.data, config.params as Record<string, unknown>, {
          requestData,
        });
      }

      return response;
    }, async (error: unknown) => {
      const recovered = await cachedGetAfterFailure(error);
      if (recovered) return recovered;
      return Promise.reject(error);
    });
  }

  function isOfflineCacheMiss(error: unknown): boolean {
    return error instanceof Error && error.message === 'offline_cache_miss';
  }

  return { install, isOfflineCacheMiss };
}
